import React from 'react'
import { graphql } from 'gatsby'
import { RichText } from 'prismic-reactjs'
import { withPrismicPreview } from 'gatsby-plugin-prismic-previews'
import { repositoryConfigs } from '../utils/prismic-previews'
import { SliceZones } from '../components/slices/slice-zones'
import Layout from '../components/global/layout'
import BlogRelatedPostsSidebar from '../components/blog/related-posts-sidebar'
import BlogSubscribeForm from '../components/blog/subscribe-form'
import CustomLink from '../utils/prismic-content-link'
import { siteMetadata } from '../site-config'

const CustomerStoriesArticle = ({ data, pageContext }) => {
  if (!data)
    return null

  const document = data.prismicCustomerStory;
  const documentsByTag = data.allPrismicCustomerStory.nodes;

  const pageUrl = `${siteMetadata.siteUrl}${document.url}`

  // The rich text content is the article body, so it renders inside the main
  // column next to the sidebar (like the insight article). Any other slices
  // are full-width components and stay below the two-column grid.
  const body = document.data.body || [];
  const richTextSlices = body.filter((slice) => slice.slice_type === 'rich_text_content');
  const otherSlices = body.filter((slice) => slice.slice_type !== 'rich_text_content');

  return (
    <Layout pageTitle={document.data.title.text}
      metaTitle={document.data.meta_title.text}
      metaDescription={document.data.meta_description.text || document.data.summary.text}
      shareImage={document.data.share_image.url}>
      <div className="c-article c-article--customer-story">
        <div className="container">
          <div className="row">
            <div className="c-article__main">
              <div className="c-article__header">
                <h1 className="c-article__header-title">{document.data.title.text}</h1>
                {document.data.summary.text ? <p className="c-article__header-summary">{document.data.summary.text}</p> : null}
              </div>
              {richTextSlices.length ?
                <div className="c-article__body cms-content">
                  {richTextSlices.map((slice, i) => (
                    <RichText key={`richtext-${i}`} render={slice.primary.richtextcontent_content.raw} serializeHyperlink={CustomLink} />
                  ))}
                </div> : null}
            </div>
            <div className="c-article__sidebar">
              <BlogSubscribeForm pageUrl={pageUrl} pageTitle={document.data.title.text}
                title="Stay up to date"
                description="Get the latest customer stories and industry insights delivered to your inbox."
                successMessage="Thank you for subscribing to our customer stories." />
              <BlogRelatedPostsSidebar heading="Related customer stories" posts={documentsByTag} />
            </div>
          </div>
        </div>
      </div>
      <div className="c-article-slices--customer-story">
        <SliceZones slices={otherSlices} />
      </div>
    </Layout>
  )
}

export const query = graphql`
  query CustomerStoriesArticleQuery($id: String, $tags: [String]) {
    prismicCustomerStory(id: { eq: $id }) {
      _previewable
      id
      uid
      lang
      type
      url
      tags
      data {
        title {
          text
        }
        subtitle {
          text
        }
        summary {
          text
        }
        meta_title {
          text
        }
        meta_description {
          text
        }
        share_image {
          url
        }
        body {
          ... on PrismicCustomerStoryDataBodyLogos {
            id
            items {
              logos_image {
                alt
                copyright
                url
                gatsbyImageData
              }
              logos_name {
                text
                html
                raw
              }
            }
            primary {
              logos_title {
                text
              }
              logos_background_image {
                url
              }
            }
            slice_type
          }
          ... on PrismicCustomerStoryDataBodyContentWithMedia {
            id
            primary {
              contentmedia_subtitle {
                text
              }
              contentmedia_title {
                text
              }
              contentmedia_content {
                text
              }
              contentmedia_image {
                url
              }
              contentmedia_button_text {
                text
              }
              contentmedia_button_link {
                url
              }
              contentmedia_button2_text {
                text
              }
              contentmedia_button2_link {
                url
              }
              contentmedia_image_alignment
            }
            slice_type
          }
          ... on PrismicCustomerStoryDataBodyCallToAction {
            id
            primary {
              cta_button_link {
                url
              }
              cta_button_text
              cta_description {
                text
              }
              cta_title {
                text
              }
            }
            slice_type
          }
          ... on PrismicCustomerStoryDataBodyRichTextContent {
            id
            primary {
              richtextcontent_content {
                raw
              }
            }
            slice_type
          }
          ... on PrismicCustomerStoryDataBodyTestimonial {
            id
            primary {
              testimonial_quote {
                text
              }
              testimonial_author {
                text
              }
              testimonial_link_text {
                text
              }
              testimonial_link {
                url
              }
            }
            slice_type
          }
          ... on PrismicCustomerStoryDataBodyWhyWorkWithUs {
            id
            primary {
              whyworkwithus_point1 {
                text
              }
              whyworkwithus_point2 {
                text
              }
              whyworkwithus_point3 {
                text
              }
              whyworkwithus_title {
                text
              }
            }
            slice_type
          }
          ... on PrismicCustomerStoryDataBodyUseCases {
            id
            items {
              usecases_usecase {
                text
              }
            }
            primary {
              usecases_link_text {
                text
              }
              usecases_title {
                text
              }
              usecases_content {
                text
              }
              usescases_link {
                url
              }
            }
            slice_type
          }
          ... on PrismicCustomerStoryDataBodyTestimonialCarousel {
            id
            items {
              testimonialcarousel_author {
                text
              }
              testimonialcarousel_authorjobtitle {
                text
              }
              testimonialcarousel_image {
                url
              }
              testimonialcarousel_quote {
                text
              }
              testimonialcarousel_title {
                text
              }
            }
            primary {
              testimonialcarousel_title {
                text
              }
            }
            slice_type
          }
          ... on PrismicCustomerStoryDataBodyHowItWorksCarousel {
            id
            primary {
              howitworkscarousel_title {
                text
              }
            }
            items {
              howitworkscarousel_title {
                text
              }
              howitworkscarousel_summary {
                text
              }
            }
            slice_type
          }
          ... on PrismicCustomerStoryDataBodyFeaturedProjects {
            id
            primary {
              featuredprojects_startprojectlink {
                url
              }
              featuredprojects_title {
                text
              }
              display_tag
            }
            items {
              featuredprojects_document {
                document {
                  ... on PrismicProject {
                    id
                    data {
                      image {
                        url
                      }
                      summary {
                        text
                      }
                      title {
                        text
                      }
                      industry
                      service
                    }
                    tags
                  }
                  ... on PrismicCustomerStory {
                    id
                    url
                    data {
                      listing_image {
                        url
                        alt
                      }
                      title {
                        text
                      }
                      short_title {
                        text
                      }
                    }
                    tags
                  }
                }
              }
            }
            slice_type
          }
          ... on PrismicCustomerStoryDataBodyServicesCarousel {
            id
            items {
              servicescarousel_image {
                url
              }
              servicescarousel_circleimage {
                url
              }
              servicescarousel_fullscreenimage {
                url
              }
              servicescarousel_link {
                url
              }
              servicescarousel_navigationtitle {
                text
              }
              servicescarousel_summary {
                text
              }
              servicescarousel_title {
                text
              }
            }
            primary {
              servicescarousel_subtitle {
                text
              }
              servicescarousel_title {
                text
              }
              servicescarousel_summary {
                text
              }
            }
            slice_type
          }
          ... on PrismicCustomerStoryDataBodyFeaturedBlogPosts {
            id
            items {
              featuredblogposts_document {
                document {
                  ... on PrismicInsightArticle {
                    id
                    data {
                      listing_image {
                        url
                      }
                      title {
                        text
                      }
                      summary {
                        text
                      }
                    }
                    url
                    tags
                  }
                }
                type
              }
            }
            primary {
              featuredblogposts_title {
                text
              }
            }
            slice_type
          }
          ... on PrismicCustomerStoryDataBodyListing {
            id
            primary {
              listing_title {
                text
              }
              listing_buttonlink {
                url
              }
              listing_buttontext {
                text
              }
              listing_button2link {
                url
              }
              listing_button2text {
                text
              }
            }
            items {
              image {
                url
              }
              listing_title {
                text
              }
            }
            slice_type
          }
        }
      }
    }
    allPrismicCustomerStory (
      limit: 10
      filter: {tags: {in: $tags}, id: {ne: $id}}
    ) {
      nodes {
        data {
          summary {
            text
          }
          title {
            text
          }
          listing_image {
            alt
            url
          }
        }
        url
        tags
      }
    }
  }
`

export default withPrismicPreview(CustomerStoriesArticle, repositoryConfigs)